// src/pages/ProfilePage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  Chip,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Badge,
  ListItemIcon,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  School as SchoolIcon,
  LocationOn as LocationOnIcon,
  Edit as EditIcon,
  Article as ArticleIcon,
  Event as EventIcon,
  Folder as FolderIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
} from '@mui/icons-material';

// ==================== ИМПОРТЫ API ====================
import { authAPI, projectsAPI, API_BASE_URL } from '../services/api';
import { eventsAPI } from '../services/eventsAPI';
import { useAuth } from '../contexts/AuthContext';

// ==================== ИМПОРТЫ ДАННЫХ ====================
import { POSITIONS } from '../data/scienceData';
import { getCompetencyIcon, getFieldColor } from '../data/themeData';

// ==================== ИМПОРТ УТИЛИТЫ ДЛЯ ЦВЕТОВ ====================
import { getHeaderGradient } from '../utils/getImageColors';

// ==================== КОМПОНЕНТЫ КАРТОЧЕК ====================
import ProfileProjectCard from '../components/Profile/ProfileProjectCard';
import ProfileEventCard from '../components/Profile/ProfileEventCard';
import ProfileDocumentsTab from '../components/Profile/ProfileDocumentsTab';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';

// ==================== МОДАЛКИ ====================
import EditProfileModal from '../components/Profile/EditProfileModal';
import EditAvatarModal from '../components/Profile/EditAvatarModal';
import AllProjectsModal from '../components/Profile/AllProjectsModal';
import AllEventsModal from '../components/Profile/AllEventsModal';
import AllDocumentsModal from '../components/Profile/AllDocumentsModal';
import UploadDocumentModal from '../components/Profile/UploadDocumentModal';
import InviteModal from '../components/InviteModal/InviteModal';

// ==================== ВСПОМОГАТЕЛЬНЫЕ КОМПОНЕНТЫ ====================

const EditPhoneDialog = ({ open, onClose, user, onSave }) => {
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (user) {
      setPhone(user.phone || '');
    }
  }, [user]);

  const handleSave = () => {
    onSave({ phone });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="div" fontWeight="600">
          Редактировать телефон
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          label="Телефон"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+7 (999) 123-45-67"
          variant="outlined"
          InputProps={{
            startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />,
          }}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// 🔥 Компонент для горизонтального скролла со стрелками
const ScrollableRow = ({ children }) => {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const check = () => {
      setShowLeft(el.scrollLeft > 10);
      setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
    };

    check();
    el.addEventListener('scroll', check);
    window.addEventListener('resize', check);
    return () => {
      el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [children]);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {showLeft && (
        <IconButton
          onClick={() => scroll(-1)}
          sx={{
            position: 'absolute',
            left: -12,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'background.paper',
            boxShadow: 1,
            '&:hover': { backgroundColor: 'grey.100' }
          }}
          size="small"
        >
          <ChevronLeftIcon />
        </IconButton>
      )}

      {showRight && (
        <IconButton
          onClick={() => scroll(1)}
          sx={{
            position: 'absolute',
            right: -12,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'background.paper',
            boxShadow: 1,
            '&:hover': { backgroundColor: 'grey.100' }
          }}
          size="small"
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'visible',  // 🔥 не обрезаем анимацию стопок
          gap: 4,
          pb: 3,                  // 🔥 отступ снизу для тени
          pt: 3,                  // 🔥 отступ сверху для анимации подъёма
          mx: 2,                  // 🔥 отступы для стрелок
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { height: '8px', background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '4px' },
          '&:hover::-webkit-scrollbar-thumb': { background: 'grey.400' },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
// ==================== ОСНОВНОЙ КОМПОНЕНТ ====================

const ProfilePage = () => {
  const { userId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  const [headerGradient, setHeaderGradient] = useState(null);
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [editPhoneDialogOpen, setEditPhoneDialogOpen] = useState(false);
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState(null);
  const [editAvatarModalOpen, setEditAvatarModalOpen] = useState(false);

  // Модалки для «Показать все»
  const [allProjectsModalOpen, setAllProjectsModalOpen] = useState(false);
  const [allEventsModalOpen, setAllEventsModalOpen] = useState(false);
  const [allDocumentsModalOpen, setAllDocumentsModalOpen] = useState(false);
  const [uploadDocumentModalOpen, setUploadDocumentModalOpen] = useState(false);
  const [documentsRefreshTrigger, setDocumentsRefreshTrigger] = useState(0);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    middle_name: ''
  });

  const isOwnProfile = currentUser && user && currentUser.id === user.id;

  // ==================== ЗАГРУЗКА ДАННЫХ ====================

  useEffect(() => {
    loadUserData();
  }, [userId, currentUser]);

  useEffect(() => {
    if (user?.avatar) {
      const avatarUrl = getAvatarUrl();
      if (avatarUrl) {
        getHeaderGradient(avatarUrl).then(gradient => {
          setHeaderGradient(gradient);
        }).catch(() => {
          setHeaderGradient(null);
        });
      }
    } else {
      setHeaderGradient(null);
    }
  }, [user?.avatar]);

  const loadUserData = async () => {
    try {
      setLoading(true);

      let userData;
      if (userId) {
        userData = await authAPI.getUserById(userId);
      } else {
        userData = await authAPI.getProfile();
      }

      setUser(userData);
      setEditFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        middle_name: userData.middle_name || ''
      });

      await loadUserProjects(userData.id);
      await loadUserEvents(userData.id);

    } catch (err) {
      console.error('❌ Ошибка загрузки профиля:', err);
      setError('Не удалось загрузить данные профиля');
    } finally {
      setLoading(false);
    }
  };

  const loadUserProjects = async (userId) => {
    try {
      const projectsData = await projectsAPI.getAll();
      let projectsList = [];
      if (Array.isArray(projectsData)) {
        projectsList = projectsData;
      } else if (projectsData.results) {
        projectsList = projectsData.results;
      }

      const userProjects = projectsList.filter(p => {
        const isOwner = p.owner?.id === userId;
        const isMember = p.members?.some(m => m.id === userId || m.user?.id === userId);
        return isOwner || isMember;
      });

      setProjects(userProjects);
    } catch (err) {
      console.error('❌ Ошибка загрузки проектов:', err);
    }
  };

  const loadUserEvents = async (userId) => {
    try {
      const eventsData = await eventsAPI.getUserEvents(userId);
      setEvents(eventsData);
    } catch (err) {
      console.error('❌ Ошибка загрузки мероприятий:', err);
      setEvents([]);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // ==================== ОБРАБОТЧИКИ ====================

  const handleEditProfileOpen = () => {
    if (!isOwnProfile) return;
    setEditProfileModalOpen(true);
  };

  const handlePhoneEditSave = async (data) => {
    try {
      const updatedUser = await authAPI.updateProfile(data);
      setUser({ ...user, ...updatedUser, avatar: user.avatar });
    } catch (err) {
      console.error('❌ Ошибка обновления телефона:', err);
    }
  };

  const handleAvatarMenuOpen = (event) => {
    if (!isOwnProfile) return;
    setAvatarMenuAnchor(event.currentTarget);
  };

  const handleAvatarMenuClose = () => {
    setAvatarMenuAnchor(null);
  };

  const handleAvatarUploadClick = () => {
    handleAvatarMenuClose();
    setEditAvatarModalOpen(true);
  };

  const handleAvatarSave = async (file) => {
    try {
      await authAPI.uploadAvatar(file);
      const freshUser = await authAPI.getProfile();
      setUser(freshUser);
      return freshUser;
    } catch (err) {
      console.error('❌ Ошибка загрузки аватарки:', err);
      throw err;
    }
  };

  const handleAvatarDelete = async () => {
    handleAvatarMenuClose();
    try {
      await authAPI.deleteAvatar();
      setUser(prev => ({ ...prev, avatar: null }));
    } catch (err) {
      console.error('❌ Ошибка удаления аватарки:', err);
    }
  };

  // ==================== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ====================

  const getInitials = () => {
    if (!user) return '?';
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user.first_name) return user.first_name[0].toUpperCase();
    if (user.email) return user.email[0].toUpperCase();
    return '?';
  };

  const getFullName = () => {
    const parts = [];
    if (user?.last_name) parts.push(user.last_name);
    if (user?.first_name) parts.push(user.first_name);
    if (user?.middle_name) parts.push(user.middle_name);
    return parts.join(' ') || 'Пользователь';
  };

  const getRoleDisplay = (position) => {
    if (!position) return 'Не указано';
    const roleMap = {
      'student': 'Студент',
      'spo_student': 'Студент колледжа (СПО)',
      'bachelor': 'Студент бакалавриата',
      'master': 'Студент магистратуры',
      'postgraduate': 'Аспирант',
      'doctoral': 'Докторант',
      'lecturer': 'Преподаватель',
      'senior_lecturer': 'Старший преподаватель',
      'docent': 'Доцент',
      'professor': 'Профессор',
      'head_of_department': 'Заведующий кафедрой',
      'dean': 'Декан факультета',
      'researcher': 'Научный сотрудник',
      'senior_researcher': 'Ведущий научный сотрудник'
    };
    return roleMap[position] || position;
  };

  const getBranchName = () => {
    if (!user?.branch) return 'Филиал не указан';
    if (user.branch_detail?.name) return user.branch_detail.name;
    return user.branch;
  };

  const getStudyProgramsList = () => {
    if (!user) return [];
    if (user.study_programs && Array.isArray(user.study_programs) && user.study_programs.length > 0) {
      return user.study_programs;
    }
    if (user.study_program_detail) {
      return [user.study_program_detail];
    }
    return [];
  };

  const getDepartmentName = () => {
    if (!user?.department) return null;
    if (user.department_detail?.name) return user.department_detail.name;
    return user.department;
  };

  const getAvatarUrl = () => {
    if (!user?.avatar) return null;
    if (user.avatar.startsWith('http')) return user.avatar;
    return `${API_BASE_URL}${user.avatar}`;
  };

  // ==================== РЕНДЕРИНГ ====================

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Загрузка профиля...</Typography>
      </Container>
    );
  }

  if (error || !user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Профиль не найден'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Вернуться назад
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ mb: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box sx={{ width: '100%', maxWidth: '1024px', px: { xs: 2, sm: 3 } }}>

        {/* Кнопка назад */}
        {!isOwnProfile && (
          <Button onClick={() => navigate(-1)} sx={{
            position: 'fixed', left: { xs: '-100%', sm: 16, md: 24 }, top: { xs: 0, sm: 80, md: 90 },
            zIndex: 1200, backgroundColor: 'rgba(255,255,255,0.95)', color: 'primary.main',
            borderRadius: 5, '&:hover': { backgroundColor: 'white' },
            display: { xs: 'none', sm: 'flex' }, minWidth: '40px', width: '40px', height: '40px',
            p: 0, boxShadow: 'none', border: '1px solid', borderColor: 'rgba(0,0,0,0.08)'
          }}>
            <ArrowBackIcon fontSize="small" />
          </Button>
        )}

        {/* Шапка профиля */}
        <Paper elevation={0} sx={{ mb: 4, overflow: 'hidden', borderRadius: 8, position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0,
            background: headerGradient || `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.7)} 0%, ${alpha(theme.palette.primary.dark, 0.8)} 100%)`,
            transition: 'background 0.3s ease'
          }} />
          <Box sx={{ height: { xs: 80, sm: 120 }, width: '100%', position: 'relative', zIndex: 2 }} />

          {isOwnProfile ? (
              <IconButton onClick={() => navigate('/questionnaire/3', {
                state: { from: { pathname: window.location.pathname } }
              })}
                sx={{ position: 'absolute', top: { xs: 8, sm: 16 }, right: { xs: 8, sm: 16 }, color: 'white', backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }, zIndex: 10 }}>
                <SettingsIcon />
              </IconButton>
            ) : (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setInviteModalOpen(true);
                }}
                variant="contained"
                startIcon={<EmailIcon />}
                sx={{
                  position: 'absolute',
                  top: { xs: 8, sm: 16 },
                  right: { xs: 8, sm: 16 },
                  zIndex: 10,
                  backgroundColor: '#2c3e50',
                  color: 'white',
                  borderRadius: 6,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  px: 2,
                  py: 0.8,
                  '&:hover': { backgroundColor: '#1e2b37' }
                }}
              >
                Пригласить
              </Button>
            )}

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' }, gap: { xs: 2, sm: 4 }, px: { xs: 2, sm: 4 }, pt: { xs: 5, sm: 10 }, pb: { xs: 3, sm: 4 }, mt: { xs: -6, sm: -8 }, position: 'relative', width: '100%', zIndex: 5 }}>
            <Box sx={{ flex: { xs: '0 0 auto', sm: '0 0 180px' }, display: 'flex', justifyContent: { xs: 'center', sm: 'center' }, width: { xs: '100%', sm: 'auto' } }}>
              {isOwnProfile ? (
                <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <IconButton size="small" onClick={handleAvatarMenuOpen}
                      sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.100' }, width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}>
                      <PhotoCameraIcon fontSize="small" />
                    </IconButton>
                  }>
                  <Avatar src={getAvatarUrl()} onClick={handleAvatarMenuOpen}
                    sx={{ width: { xs: 100, sm: 120 }, height: { xs: 100, sm: 120 }, border: '4px solid white', boxShadow: theme.shadows[3], bgcolor: 'white', color: theme.palette.primary.main, fontSize: { xs: '2rem', sm: '2.5rem' }, '& img': { objectFit: 'cover' } }}>
                    {!user?.avatar && getInitials()}
                  </Avatar>
                </Badge>
              ) : (
                <Avatar src={getAvatarUrl()}
                  sx={{ width: { xs: 100, sm: 120 }, height: { xs: 100, sm: 120 }, border: '4px solid white', boxShadow: theme.shadows[3], bgcolor: 'white', color: theme.palette.primary.main, fontSize: { xs: '2rem', sm: '2.5rem' }, '& img': { objectFit: 'cover' } }}>
                  {!user?.avatar && getInitials()}
                </Avatar>
              )}

              {isOwnProfile && (
                <Menu anchorEl={avatarMenuAnchor} open={Boolean(avatarMenuAnchor)} onClose={handleAvatarMenuClose} PaperProps={{ sx: { width: 200 } }}>
                  <MenuItem onClick={handleAvatarUploadClick}>
                    <ListItemIcon><PhotoCameraIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Загрузить фото</ListItemText>
                  </MenuItem>
                  {user?.avatar && (
                    <MenuItem onClick={handleAvatarDelete} sx={{ color: 'error.main' }}>
                      <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                      <ListItemText>Удалить фото</ListItemText>
                    </MenuItem>
                  )}
                </Menu>
              )}
            </Box>

            <Box sx={{ flex: 1, color: 'white', textAlign: { xs: 'center', sm: 'left' }, width: { xs: '100%', sm: 'auto' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Typography variant="h4" fontWeight="600" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
                  {getFullName()}
                </Typography>
                {isOwnProfile && (
                  <IconButton size="small" onClick={handleEditProfileOpen} sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Typography variant="subtitle1" sx={{ opacity: 0.9, fontWeight: 400, mb: 1 }}>
                {getRoleDisplay(user.position)}
              </Typography>
              {user.bio && <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>{user.bio}</Typography>}
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Chip icon={<LocationOnIcon sx={{ fontSize: 16 }} />} label={getBranchName()} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', height: 24, '& .MuiChip-icon': { color: 'white' } }} />
                {user.academic_degree && (
                  <Chip icon={<SchoolIcon sx={{ fontSize: 16 }} />} label={user.academic_degree} size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', height: 24, '& .MuiChip-icon': { color: 'white' } }} />
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        <EditPhoneDialog open={editPhoneDialogOpen} onClose={() => setEditPhoneDialogOpen(false)} user={user} onSave={handlePhoneEditSave} />

        <EditProfileModal open={editProfileModalOpen} onClose={() => setEditProfileModalOpen(false)} user={user}
          onSave={async (updatedUser) => {
            try {
              const freshUser = await authAPI.getProfile();
              setUser(freshUser);
            } catch (err) {
              setUser({ ...updatedUser, avatar: updatedUser.avatar || user.avatar });
            }
          }} />

        <EditAvatarModal open={editAvatarModalOpen} onClose={() => setEditAvatarModalOpen(false)} onSave={handleAvatarSave} currentAvatarUrl={getAvatarUrl()} />

        {/* НОВЫЙ СЕТОЧНЫЙ МАКЕТ */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          mb: 3,
          width: '100%'
        }}>

          {/* СТРОКА 1: Контакты (30%) + Компетенции (70%) */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
            width: '100%'
          }}>
            {/* Контакты - 35% */}
            <Box sx={{
              width: { xs: '100%', sm: '35%' },
              minWidth: 0,
              flexShrink: 1,
              flexGrow: 0
            }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 6,
                  boxShadow: 'none',
                  height: '100%',
                  overflow: 'hidden',
                  wordBreak: 'break-word'
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color="primary"
                  sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Контакты
                </Typography>
                <List dense sx={{ width: '100%' }}>
                  <ListItem sx={{ px: 0, flexWrap: 'wrap' }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', width: 40, height: 40 }}>
                        <EmailIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary="Email"
                      secondary={user.email}
                      primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { wordBreak: 'break-all' } }}
                    />
                  </ListItem>
                  <ListItem
                    sx={{ px: 0, flexWrap: 'wrap' }}
                    secondaryAction={
                      isOwnProfile && (
                        <IconButton edge="end" size="small" onClick={() => setEditPhoneDialogOpen(true)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', width: 40, height: 40 }}>
                        <PhoneIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary="Телефон"
                      secondary={user.phone || 'Не указан'}
                      primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { wordBreak: 'break-all' } }}
                    />
                  </ListItem>
                </List>
              </Paper>
            </Box>

            {/* Компетенции - 65% */}
            <Box sx={{
              width: { xs: '100%', sm: '65%' },
              minWidth: 0,
              flexShrink: 1,
              flexGrow: 1
            }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 6,
                  boxShadow: 'none',
                  height: '100%',
                  overflow: 'hidden'
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color="primary"
                  sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Компетенции
                </Typography>
                <Box sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  overflow: 'hidden'
                }}>
                  {user.competencies?.length > 0 ? (
                    user.competencies.map((comp, index) => (
                      <Chip
                        key={index}
                        label={`${getCompetencyIcon(comp)} ${comp}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: 'success.main',
                          height: 24,
                          fontSize: '0.75rem',
                          borderRadius: 6,
                          pl: 0.5,
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word'
                          }
                        }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Компетенции не указаны
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* СТРОКА 2: Основное (40%) + Научные интересы (60%) */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
            width: '100%'
          }}>
            {/* Основное - 40% */}
            <Box sx={{
              width: { xs: '100%', sm: '40%' },
              minWidth: 0,
              flexShrink: 1,
              flexGrow: 1
            }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 6,
                  boxShadow: 'none',
                  height: '100%',
                  overflow: 'hidden'
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color="primary"
                  sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Основное
                </Typography>

                {/* Направления подготовки */}
                <Box sx={{
                  width: '100%',
                  mb: 2,
                  textAlign: 'left'
                }}>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5, textAlign: 'left' }}>
                    Направления подготовки
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-start' }}>
                    {getStudyProgramsList().length > 0 ? (
                      getStudyProgramsList().map((program, index) => (
                        <Chip
                          key={program.id || index}
                          label={`${program.name} (${program.code})`}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: 'info.main',
                            height: 'auto',
                            minHeight: 24,
                            fontSize: '0.7rem',
                            borderRadius: 6,
                            '& .MuiChip-label': {
                              whiteSpace: 'normal',
                              wordBreak: 'break-word'
                            }
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        Не указано
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Кафедра */}
                {getDepartmentName() && (
                  <Box sx={{ width: '100%', textAlign: 'left' }}>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5, textAlign: 'left' }}>
                      Кафедра
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'left' }}>
                      {getDepartmentName()}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>

            {/* Научные интересы - 60% */}
            <Box sx={{
              width: { xs: '100%', sm: '60%' },
              minWidth: 0,
              flexShrink: 1,
              flexGrow: 1
            }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 6,
                  boxShadow: 'none',
                  height: '100%',
                  overflow: 'hidden'
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color="primary"
                  sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Научные интересы
                </Typography>
                <Box sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  overflow: 'hidden'
                }}>
                  {user.research_fields?.length > 0 ? (
                    user.research_fields.map((interest, index) => (
                      <Chip
                        key={index}
                        label={interest}
                        size="small"
                        sx={{
                          bgcolor: getFieldColor(interest),
                          color: '#1a1a1a',
                          height: 'auto',
                          minHeight: 24,
                          fontSize: '0.75rem',
                          borderRadius: 6,
                          fontWeight: 500,
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word'
                          }
                        }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Научные интересы не указаны
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* СТРОКА 3: Публикации (33%) + Методологии (33%) + Сотрудничество (33%) */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
            width: '100%'
          }}>
            {/* Публикации - 33% */}
            <Box sx={{
              width: { xs: '100%', sm: '33.333%' },
              minWidth: 0,
              flexShrink: 1,
              flexGrow: 1
            }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 6,
                  boxShadow: 'none',
                  height: '100%',
                  overflow: 'hidden'
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  color="primary"
                  sx={{ mb: 1.5, fontSize: { xs: '1rem', sm: '1.1rem' } }}
                >
                  Публикации
                </Typography>
                <Typography variant="h5" fontWeight="600" color="primary" sx={{ mb: 1.5 }}>
                  {user.publications_count || 0}
                </Typography>
                {user.publication_types?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {user.publication_types.slice(0, 3).map((type, index) => (
                      <Chip
                        key={index}
                        label={type}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.warning.main, 0.1),
                          color: 'warning.main',
                          height: 'auto',
                          minHeight: 22,
                          fontSize: '0.7rem',
                          borderRadius: 6,
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word'
                          }
                        }}
                      />
                    ))}
                    {user.publication_types.length > 3 && (
                      <Chip
                        label={`+${user.publication_types.length - 3}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', borderRadius: 6 }}
                      />
                    )}
                  </Box>
                )}
              </Paper>
            </Box>

            {/* Методологии - 33% */}
            <Box sx={{
              width: { xs: '100%', sm: '33.333%' },
              minWidth: 0,
              flexShrink: 1,
              flexGrow: 1
            }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 6,
                  boxShadow: 'none',
                  height: '100%',
                  overflow: 'hidden'
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  color="primary"
                  sx={{ mb: 1.5, fontSize: { xs: '1rem', sm: '1.1rem' } }}
                >
                  Методологии
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {user.methodologies?.length > 0 ? (
                    user.methodologies.slice(0, 4).map((method, index) => (
                      <Chip
                        key={index}
                        label={method}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.secondary.main, 0.1),
                          color: 'secondary.main',
                          height: 'auto',
                          minHeight: 22,
                          fontSize: '0.7rem',
                          borderRadius: 6,
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word'
                          }
                        }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Не указаны
                    </Typography>
                  )}
                  {user.methodologies?.length > 4 && (
                    <Chip
                      label={`+${user.methodologies.length - 4}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem', borderRadius: 6 }}
                    />
                  )}
                </Box>
              </Paper>
            </Box>

            {/* Сотрудничество - 33% */}
            <Box sx={{
              width: { xs: '100%', sm: '33.333%' },
              minWidth: 0,
              flexShrink: 1,
              flexGrow: 1
            }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 6,
                  boxShadow: 'none',
                  height: '100%',
                  overflow: 'hidden'
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  color="primary"
                  sx={{ mb: 1.5, fontSize: { xs: '1rem', sm: '1.1rem' } }}
                >
                  Сотрудничество
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {user.collaboration_types?.length > 0 ? (
                    user.collaboration_types.slice(0, 4).map((type, index) => (
                      <Chip
                        key={index}
                        label={type}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.error.main, 0.1),
                          color: 'error.main',
                          height: 'auto',
                          minHeight: 22,
                          fontSize: '0.7rem',
                          borderRadius: 6,
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word'
                          }
                        }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Не указаны
                    </Typography>
                  )}
                  {user.collaboration_types?.length > 4 && (
                    <Chip
                      label={`+${user.collaboration_types.length - 4}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem', borderRadius: 6 }}
                    />
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* СТРОКА 4: Опыт участия в проектах (100%) - ТОЛЬКО ЕСЛИ ЕСТЬ */}
          {user.projects_experience && (
            <Box sx={{
              width: '100%',
              minWidth: 0,
              mt: 1
            }}>
              <Typography
                variant="h6"
                fontWeight="600"
                color="primary"
                sx={{ mb: 1.5, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                Опыт участия в проектах
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  textAlign: 'left',
                  color: 'text.secondary'
                }}
              >
                {user.projects_experience}
              </Typography>
            </Box>
          )}

        </Box>

      {/* 🔥 ВКЛАДКИ С КНОПКАМИ В СТРОКЕ ЗАГОЛОВКА */}
        <Box sx={{ width: '100%', mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pt: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={{
                minHeight: 48,
                '& .MuiTab-root': {
                  minHeight: 48, py: 1, px: 3, borderRadius: 6,
                  transition: 'all 0.2s', color: 'text.secondary',
                  '&:hover': { color: 'primary.main', backgroundColor: alpha(theme.palette.primary.main, 0.05) }
                },
                '& .Mui-selected': {
                  color: 'primary.main !important', backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)', fontWeight: 600
                }
              }}
            >
              <Tab icon={<FolderIcon sx={{ fontSize: 20 }} />} label="Проекты" iconPosition="start" />
              <Tab icon={<EventIcon sx={{ fontSize: 20 }} />} label="Мероприятия" iconPosition="start" />
              <Tab icon={<ArticleIcon sx={{ fontSize: 20 }} />} label="Документы" iconPosition="start" />
            </Tabs>

            {/* 🔥 КНОПКИ СПРАВА */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Кнопка «+» только на вкладке Документы и только в своём профиле */}
              {tabValue === 2 && isOwnProfile && (
                <IconButton
                  onClick={() => setUploadDocumentModalOpen(true)}
                  sx={{ bgcolor: theme.palette.primary.main, color: 'white', width: 40, height: 40, '&:hover': { bgcolor: theme.palette.primary.dark } }}
                >
                  <AddIcon />
                </IconButton>
              )}

              {/* Кнопка «Показать все» — разная для каждой вкладки */}
              {(tabValue === 0 && projects.length > 0) && (
                <Button variant="outlined" onClick={() => setAllProjectsModalOpen(true)} sx={{ borderRadius: 5 }}>
                  Показать все
                </Button>
              )}
              {(tabValue === 1 && events.length > 0) && (
                <Button variant="outlined" onClick={() => setAllEventsModalOpen(true)} sx={{ borderRadius: 5 }}>
                  Показать все
                </Button>
              )}
              {(tabValue === 2 && true) && (
                <Button variant="outlined" onClick={() => setAllDocumentsModalOpen(true)} sx={{ borderRadius: 5 }}>
                  Показать все
                </Button>
              )}
            </Box>
          </Box>

          {/* Контент вкладок */}
          <Box>
            {/* 🔥 Проекты */}
            <TabPanel value={tabValue} index={0}>
              {projects.length > 0 ? (
                <ScrollableRow>
                  {projects.map((project) => (
                    <Box key={project.id} sx={{
                      minWidth: '280px',
                      maxWidth: '310px',
                      flexShrink: 0,
                    }}>
                      <ProfileProjectCard
                        project={project}
                        onClick={() => navigate(`/projects/${project.id}`)}
                      />
                    </Box>
                  ))}
                </ScrollableRow>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <FolderIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Нет активных проектов
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Пользователь пока не участвует ни в одном проекте
                  </Typography>
                </Box>
              )}
            </TabPanel>

            {/* 🔥 Мероприятия */}
            <TabPanel value={tabValue} index={1}>
              {events.length > 0 ? (
                <ScrollableRow>
                  {events.map((event) => (
                    <Box key={event.id} sx={{
                      minWidth: '280px',
                      maxWidth: '310px',
                      flexShrink: 0,
                    }}>
                      <ProfileEventCard
                        event={event}
                        onClick={() => navigate(`/events/${event.id}`)}
                      />
                    </Box>
                  ))}
                </ScrollableRow>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <EventIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Нет мероприятий
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Здесь будут отображаться мероприятия, на которые подписан пользователь
                  </Typography>
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <ProfileDocumentsTab
                  isOwnProfile={isOwnProfile}
                  userId={user?.id}
                  refreshTrigger={documentsRefreshTrigger}
                />
            </TabPanel>
          </Box>
        </Box>

        {/* МОДАЛЬНЫЕ ОКНА */}
        <AllProjectsModal open={allProjectsModalOpen} onClose={() => setAllProjectsModalOpen(false)} projects={projects} />
        <AllEventsModal open={allEventsModalOpen} onClose={() => setAllEventsModalOpen(false)} events={events} />
        <AllDocumentsModal open={allDocumentsModalOpen} onClose={() => setAllDocumentsModalOpen(false)} documents={[]} isOwnProfile={isOwnProfile} userId={user?.id} onDocumentUpdate={() => {}} />
        <UploadDocumentModal
          open={uploadDocumentModalOpen}
          onClose={() => setUploadDocumentModalOpen(false)}
          onSuccess={() => {
            setUploadDocumentModalOpen(false);
            setDocumentsRefreshTrigger(prev => prev + 1);
          }}
        />
        <InviteModal
          open={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          user={user}
        />

      </Box>
    </Container>
  );
};

export default ProfilePage;